import {
  DataType,
  isSelectableChartType,
  resolveDataType,
} from '@revelio/core';
import type { PlotBreakdownLevels } from '@revelio/core/global-ai-assistant/breakdown-levels';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { isFlatTreemapSegment } from '../../../filters/utils/treemapDrilldownLevels';
import { getIcicleSegment } from '../../Chart/Icicle/icicle-segment';
import { getPrimaryDimension } from '../../ChartConfig/utils/table-columns';
import { TOP_N_LIMIT } from '../../DataProvider/constants';
import { dimensionFromPrimaryFilter } from '../../DataResolver/convert-composition-v4-data';
import { isSingleEntityPlot } from '../../PageState/utils/is-single-entity-chart';
import {
  COLUMN_LEVELS,
  type ColumnDimension,
  isColumnLevel,
  type SecondaryDimensionValues,
} from '../types/dimension';
import {
  isShareOfSegmentPlot,
  PlotFilterType,
  type PlotOptions,
  type TableColumnConfig,
} from '../types/item';
import { isCompositionMetric, type MetricValues } from '../types/metric';

type DimensionColumn = Extract<TableColumnConfig, { kind: 'dimension' }>;
type VariableColumn = Extract<TableColumnConfig, { kind: 'variable' }>;

const resolvedDataTypeOf = (plot: PlotOptions): DataType | undefined => {
  const { chartType, secondaryDimension, dimensionSource } = plot;
  const metric = plot.defaultMetricOptions?.[0];
  if (metric === undefined || !isSelectableChartType(chartType)) {
    return undefined;
  }
  const resolution = resolveDataType(
    chartType,
    metric,
    secondaryDimension,
    dimensionSource
  );
  return resolution.compatible ? resolution.resolvedDataType : undefined;
};

const isTemporal = (plot: PlotOptions): boolean => {
  const dataType = resolvedDataTypeOf(plot);
  return (
    dataType === DataType.TIMESERIES ||
    dataType === DataType.BREAKDOWN_TIMESERIES
  );
};

export const dimensionColumn = (
  dimension: ColumnDimension,
  id = `dimension:${dimension}`
): DimensionColumn => ({ id, kind: 'dimension', dimension });

export const variableColumn = (metric: MetricValues): VariableColumn => ({
  id: `variable:${metric}`,
  kind: 'variable',
  metric,
});

/**
 * TODO: remove when all charts support tableColumns. Until then it ignores the
 * `tableColumns` every chart now saves: the planner moves onto them on purpose.
 */
export const getPlotTableColumns = (
  plot: PlotOptions | undefined,
  entityDimension: SecondaryDimensionValues
): TableColumnConfig[] => {
  if (!plot) return [];

  const { dimensionSource = 'primary', secondaryDimension } = plot;
  const dimensions = new Set<SecondaryDimensionValues>();
  dimensions.add(
    dimensionSource === 'primary' ? entityDimension : dimensionSource
  );
  if (secondaryDimension) dimensions.add(secondaryDimension);
  if (isTemporal(plot)) dimensions.add('timeseries');

  return [
    ...[...dimensions].map((dimension) => dimensionColumn(dimension)),
    ...(plot.defaultMetricOptions ?? []).map(variableColumn),
  ];
};

/** A chart's table: its columns, and whether it shows one picked entity. */
type TableDefinition = {
  tableColumns: TableColumnConfig[];
  isSingleEntity: boolean;
};

const plotFilterValue = (
  plot: PlotOptions,
  type: PlotFilterType
): number | undefined => {
  const value = plot.plotFilters?.find((filter) => filter.type === type)?.value;
  return value !== undefined && Number.isFinite(value) && value > 0
    ? value
    : undefined;
};

const breakdownLevelOf = (
  dimension: SecondaryDimensionValues,
  levels: PlotBreakdownLevels | undefined
): string | undefined => {
  switch (dimension) {
    case 'role':
      return levels?.role === undefined ? undefined : `role_lvl_${levels.role}`;
    case 'skills':
      return levels?.skills === undefined
        ? undefined
        : `skill_lvl_${levels.skills}`;
    case 'rics':
      return levels?.rics === undefined ? undefined : `rics_k${levels.rics}`;
    case 'geography':
      return levels?.geography === 'metro' ? 'msa' : levels?.geography;
    default:
      return undefined;
  }
};

// A breakdown level the pickers don't offer can't be named by a column, so
// the column stays generic.
const columnDimensionOf = (
  dimension: SecondaryDimensionValues,
  levels: PlotBreakdownLevels | undefined
): ColumnDimension => {
  const level = breakdownLevelOf(dimension, levels);
  return level !== undefined && isColumnLevel(level) ? level : dimension;
};

// A taxonomy's next level: a repeated generic column, or the explicit level
// after this one. Dimensions without levels (gender, seniority…) have none.
const sublevelOf = (
  dimension: ColumnDimension
): ColumnDimension | undefined => {
  if (!isColumnLevel(dimension)) {
    return dimension in COLUMN_LEVELS ? dimension : undefined;
  }
  const ladder: readonly string[] =
    Object.values(COLUMN_LEVELS).find((levels: readonly string[]) =>
      levels.includes(dimension)
    ) ?? [];
  const next = ladder[ladder.indexOf(dimension) + 1];
  return next !== undefined && isColumnLevel(next) ? next : undefined;
};

const isTopNSource = (plot: PlotOptions): boolean =>
  plot.dimensionSource !== undefined &&
  plot.dimensionSource !== 'primary' &&
  plot.dimensionSource !== 'timeseries';

type Ranked = 'outer' | 'inner' | undefined;

// Which column `plotFilters` rank on the chart's data set today: the outer
// one is the bars (the page's entities or a top-N source), the inner one the
// segments within them, swapped by `groupBySegment`.
const rankedColumnOf = (plot: PlotOptions): Ranked => {
  if (isTopNSource(plot)) return 'outer';
  switch (resolvedDataTypeOf(plot)) {
    case DataType.SINGLE:
      return 'outer';
    case DataType.BREAKDOWN_SINGLE:
      return plot.groupBySegment ? 'outer' : 'inner';
    case DataType.BOX:
      if (!plot.secondaryDimension) return undefined;
      return plot.groupBySegment ? 'inner' : 'outer';
    default:
      return undefined;
  }
};

type BreakdownColumns = {
  outer?: DimensionColumn;
  inner?: DimensionColumn;
  time?: DimensionColumn;
};

// The page's entities are the outer column unless a top-N source replaces
// them; a segment of the entity's own taxonomy is the level below it (as the
// planner reads a repeated column).
const breakdownColumnsOf = (
  plot: PlotOptions,
  entity: SecondaryDimensionValues,
  singleEntity: boolean
): BreakdownColumns => {
  const { dimensionSource, secondaryDimension, breakdownLevels } = plot;
  const ranked = rankedColumnOf(plot);
  const ranking = {
    ...withValue(
      'topN',
      plotFilterValue(plot, PlotFilterType.TOP_N) ??
        (isTopNSource(plot) ? TOP_N_LIMIT : undefined)
    ),
    ...withValue('bottomN', plotFilterValue(plot, PlotFilterType.BOTTOM_N)),
  };
  const hideOther = plot.hideOther ? { hideOther: true } : {};

  const outerDimension =
    dimensionSource === undefined || dimensionSource === 'primary'
      ? entity
      : columnDimensionOf(dimensionSource, breakdownLevels);
  const outer =
    outerDimension === entity && singleEntity
      ? undefined
      : {
          ...dimensionColumn(outerDimension),
          ...(ranked === 'outer' && ranking),
          ...(outerDimension !== entity && hideOther),
        };
  const innerDimension =
    secondaryDimension &&
    columnDimensionOf(secondaryDimension, breakdownLevels);
  const inner =
    innerDimension === undefined
      ? undefined
      : {
          ...dimensionColumn(
            innerDimension,
            innerDimension === outerDimension
              ? `level:${innerDimension}`
              : `dimension:${innerDimension}`
          ),
          ...(ranked === 'inner' && ranking),
          ...(innerDimension !== 'timeseries' && hideOther),
        };
  const hasTime =
    outerDimension === 'timeseries' || innerDimension === 'timeseries';
  const time =
    isTemporal(plot) && !hasTime ? dimensionColumn('timeseries') : undefined;
  return { outer, inner, time };
};

const withValue = <K extends 'topN' | 'bottomN'>(
  key: K,
  value: number | undefined
): Partial<Record<K, number>> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, number>);

const presentColumns = (
  ...columns: (DimensionColumn | undefined)[]
): DimensionColumn[] =>
  columns.filter((column): column is DimensionColumn => column !== undefined);

// Tree maps and icicles: a segment, then the level below it.
const treeColumns = (
  plot: PlotOptions,
  level: DimensionColumn,
  sublevel: ColumnDimension | undefined
): DimensionColumn[] => {
  const coverage = plot.treemap?.taxonomyCoverage;
  const hideOther = coverage?.omitOther === true || level.hideOther === true;
  const covered = (
    column: DimensionColumn,
    topN: number | undefined
  ): DimensionColumn => ({
    ...column,
    ...withValue('topN', topN),
    ...(hideOther && { hideOther }),
  });
  const parent = covered(level, coverage?.topN?.groups);
  return sublevel === undefined
    ? [parent]
    : [
        parent,
        covered(
          dimensionColumn(sublevel, `sublevel:${level.dimension}`),
          coverage?.topN?.cells
        ),
      ];
};

const legacyColumns = (
  plot: PlotOptions,
  primaryFilterId: PrimaryFilterId,
  singleEntity: boolean
): TableColumnConfig[] => {
  const { chartType, secondaryDimension, breakdownLevels } = plot;
  const entity = dimensionFromPrimaryFilter(primaryFilterId);
  const entityColumns = singleEntity ? [] : [dimensionColumn(entity)];

  if (chartType === 'icicle') {
    if (secondaryDimension === 'subsidiaries') return [];
    const segment = getIcicleSegment(primaryFilterId, secondaryDimension);
    return [
      ...entityColumns,
      ...treeColumns(
        plot,
        dimensionColumn(segment, `level:${segment}`),
        segment
      ),
      variableColumn('headcount'),
      variableColumn('share_of_headcount'),
    ];
  }

  if (isShareOfSegmentPlot(plot)) {
    if (!secondaryDimension) return [];
    const segment = columnDimensionOf(secondaryDimension, breakdownLevels);
    return [
      ...entityColumns,
      dimensionColumn(
        segment,
        segment === entity ? `level:${segment}` : `dimension:${segment}`
      ),
      dimensionColumn('timeseries'),
      variableColumn('headcount'),
      variableColumn('share_of_headcount'),
    ];
  }

  // Not mapped yet. Gauges and text take their tables from their templates,
  // Snowflake charts from their saved queries, and flows need a direction the
  // legacy fields leave implicit; the rest aren't aggregate tables or route to
  // providers of their own.
  if (
    !isSelectableChartType(chartType) ||
    plot.dataSource === 'snowflake' ||
    chartType === 'gauge-v2' ||
    chartType === 'text' ||
    chartType === 'map' ||
    chartType === 'ai-summary' ||
    chartType === 'sankey' ||
    chartType === 'chord'
  ) {
    return [];
  }

  const metrics = (plot.defaultMetricOptions ?? []).map(variableColumn);
  const { outer, inner, time } = breakdownColumnsOf(plot, entity, singleEntity);

  // Only composition tree maps nest their segment's levels.
  if (
    chartType !== 'treemap' ||
    !secondaryDimension ||
    !inner ||
    !isCompositionMetric(plot.defaultMetricOptions?.[0])
  ) {
    // `groupBySegment` becomes column order: the segment groups the bars.
    const grouping = plot.groupBySegment ? [inner, outer] : [outer, inner];
    return [...presentColumns(...grouping, time), ...metrics];
  }
  const sublevel = isFlatTreemapSegment(secondaryDimension)
    ? undefined
    : sublevelOf(inner.dimension);
  return [
    ...presentColumns(outer),
    ...treeColumns(plot, inner, sublevel),
    ...presentColumns(time),
    ...metrics,
    variableColumn('share_of_headcount'),
  ];
};

const tableDefinition = (
  plot: PlotOptions,
  primaryFilterId: PrimaryFilterId
): TableDefinition => {
  const entity = getPrimaryDimension(primaryFilterId);
  const columns = plot.tableColumns ?? [];
  const hasEntityColumn = columns.some(
    (column) => column.kind === 'dimension' && column.dimension === entity
  );
  return {
    tableColumns: columns,
    isSingleEntity:
      plot.isSingleEntity === true || (!hasEntityColumn && columns.length > 0),
  };
};

/** The table a chart saved before every chart had `tableColumns` draws today. */
export const getLegacyTableDefinition = (
  plot: PlotOptions,
  primaryFilterId: PrimaryFilterId
): TableDefinition => {
  if (plot.chartType === 'table') return tableDefinition(plot, primaryFilterId);
  const isSingleEntity = isSingleEntityPlot(plot);
  return {
    tableColumns: legacyColumns(plot, primaryFilterId, isSingleEntity),
    isSingleEntity,
  };
};
